import { useEffect, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { DashboardTemplate } from 'modules/v2/templates';
import { getCustomerSubscription, getSelfServePortalLink } from 'modules/api';
import CancelMembershipButton from './CancelMembership';
import Spinner from 'modules/v2/common/AtomicDesign/atoms/Spinner';
import env from 'environments';
import { subscriptionPollingUtils } from 'modules/v2/utils/subscriptionPolling';

const PlansAndBilling = () => {
  const [isIframeLoaded, setIsIframeLoaded] = useState(false);
  const { mutate: fetchPortalUrl, data, isLoading, isError, error, reset } = useMutation(
    getSelfServePortalLink,
  );

  // Fetch Chargebee subscription for cancel button
  const {
    data: subscription,
    error: subscriptionError,
    isLoading: isLoadingSubscription,
  } = useQuery('getCustomerSubscription', getCustomerSubscription, {
    enabled: env.ENABLE_CLICK_TO_CANCEL,
    retry: false,
    onError: (err) => {
      console.error('Failed to fetch Chargebee subscription:', err);
    },
  });


  useEffect(() => {
    // Backend will identify the user from JWT token
    // No need to send customer_id - just send empty object or optional redirectUrl
    fetchPortalUrl({});

    // Start polling for subscription status changes
    subscriptionPollingUtils.startPolling();
  }, []);

  const portalUrl = data?.portalSession?.accessUrl || data?.access_url || '';
  const activeSubscription = subscription;

  // Show loading spinner if API is loading OR if iframe hasn't loaded yet
  const showLoadingSpinner = isLoading || (portalUrl && !isIframeLoaded);

  if (isLoading) {
    return (
      <DashboardTemplate hasSidebar title="Plans and billing">
        <div className="w-full h-full flex items-center justify-center">
          <div className="text-center">
            <Spinner className="mx-auto mb-4" />
            <p className="text-gray-600">Loading billing portal...</p>
          </div>
        </div>
      </DashboardTemplate>
    );
  }

  if (isError || data?.success === false) {
    const errorCode = data?.error || error?.response?.data?.error;
    const errorMessage =
      data?.message || error?.response?.data?.message || error?.message || 'Connection failed';

    // Determine if it's a "no subscription" or "wrong billing platform" error
    const isSetupError =
      errorCode === 'NO_SUBSCRIPTION' || errorCode === 'INVALID_BILLING_PLATFORM';

    return (
      <DashboardTemplate hasSidebar title="Plans and billing">
        <div className="w-full h-full flex items-center justify-center">
          <div
            className={`text-center p-6 rounded-lg max-w-md ${
              isSetupError ? 'bg-yellow-50' : 'bg-red-50'
            }`}
          >
            <svg
              className={`w-16 h-16 mx-auto mb-4 ${
                isSetupError ? 'text-yellow-500' : 'text-red-500'
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d={
                  isSetupError
                    ? 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z'
                    : 'M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
                }
              />
            </svg>
            <p
              className={`font-semibold mb-2 ${isSetupError ? 'text-yellow-800' : 'text-red-600'}`}
            >
              {isSetupError ? 'Setup Required' : 'Connection Error'}
            </p>
            <p className={`text-sm mb-4 ${isSetupError ? 'text-yellow-700' : 'text-red-500'}`}>
              {errorMessage}
            </p>
            {!isSetupError && (
              <button
                onClick={() => {
                  reset();
                  fetchPortalUrl({});
                }}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                type="button"
              >
                Try Again
              </button>
            )}
            {isSetupError && (
              <p className="text-yellow-600 text-xs mt-4">
                Support: (904) 695-9933 | Monday–Friday, 9:00 AM–6:00 PM EST
              </p>
            )}
          </div>
        </div>
      </DashboardTemplate>
    );
  }

  return (
    <DashboardTemplate hasSidebar title="Plans and billing">
      <div className="w-full h-full">
        {/* Cancel Button aligned with template title */}
        {env.ENABLE_CLICK_TO_CANCEL && (
          <div className="flex justify-end mb-4 sm:-mt-12">
            {!isLoadingSubscription && subscription ? (
              <CancelMembershipButton subscription={subscription} />
            ) : (
              <div className="text-gray-400 text-sm">Loading...</div>
            )}
          </div>
        )}

        {/* Iframe container with loading overlay */}
        <div className="relative w-full h-[800px]">
          {showLoadingSpinner && (
            <div className="absolute inset-0 flex items-center justify-center z-10">
              <div className="text-center bg-white rounded-lg shadow-xl px-12 py-10 border border-gray-100">
                <Spinner className="mx-auto mb-4" />
                <p className="text-gray-600 font-medium">Loading billing portal...</p>
              </div>
            </div>
          )}

          {portalUrl && (
            <iframe
              src={portalUrl}
              title="Chargebee Billing Portal"
              className="w-full rounded-lg border-0 h-full"
              allow="payment"
              onLoad={() => setIsIframeLoaded(true)}
            />
          )}
        </div>
      </div>
    </DashboardTemplate>
  );
};

export default PlansAndBilling;
